// src/components/AgreementAcceptModal.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Modal,
  Typography,
  Alert,
} from "@mui/material";
import { getActiveAgreement, acceptAgreement } from "../lib/apiAgreements";
import { getMyPromotions, getPromotions } from "../lib/apiPromotions";
import { API_BASE } from "../lib/api";

export default function AgreementAcceptModal({
  open,
  onClose,
  agreementCode,         // e.g. "ARTIST_PLATFORM"
  title,                 // e.g. "Artist Platform Agreement"
  workflowContext = {},  // e.g. { workflow: "artist_onboarding" }
  onAccepted,            // callback after success
}) {
  const [loading, setLoading] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoRole, setPromoRole] = useState(null);

  const addMonths = (date, months) => {
    const result = new Date(date);
    const day = result.getDate();
    result.setMonth(result.getMonth() + months);
    if (result.getDate() < day) {
      result.setDate(0);
    }
    return result;
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatPercent = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `${(num * 100).toFixed(2).replace(/\.00$/, "")}%`;
  };

  const formatCurrencyFromCents = (value) => {
    const cents = Number(value);
    if (Number.isNaN(cents)) return value;
    return `$${(cents / 100).toFixed(2)}`;
  };

  const resolveAgreementPdfUrl = (url) => {
    if (!url) return url;
    try {
      const parsed = new URL(url, window.location.origin);
      if (!parsed.pathname.startsWith("/media/")) {
        return url;
      }
      const explicitMediaBase =
        process.env.REACT_APP_MEDIA_BASE ||
        process.env.REACT_APP_BACKEND_BASE ||
        process.env.REACT_APP_API_BASE_URL ||
        "";
      const base = explicitMediaBase || API_BASE || window.location.origin;
      return `${base}${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch (err) {
      return url;
    }
  };

  const promoCode = workflowContext?.promo_code;

  const normalizePromo = (item) => {
    if (!item) return null;
    if (item.promotion && typeof item.promotion === "object") {
      return { ...item.promotion, ...item };
    }
    return item;
  };

  const getPromoEndDate = (promotion) => {
    if (!promotion) return null;
    const now = new Date();
    if (promotion.duration_months != null) {
      const months = Number(promotion.duration_months);
      if (!Number.isNaN(months)) {
        return addMonths(now, months);
      }
    }
    if (promotion.duration_days != null) {
      const days = Number(promotion.duration_days);
      if (!Number.isNaN(days)) {
        const end = new Date(now);
        end.setDate(end.getDate() + days);
        return end;
      }
    }
    if (promotion.expires_at) {
      const exp = new Date(promotion.expires_at);
      if (!Number.isNaN(exp.getTime())) return exp;
    }
    return null;
  };

  useEffect(() => {
    if (!open) return;
    setChecked(false);
    setError("");
    setAgreement(null);
    const contextPromo = workflowContext?.promo_preview || workflowContext?.promo_details;
    setPromo(contextPromo || null);
    setPromoRole(
      contextPromo?.eligible_role
        ? String(contextPromo.eligible_role).toUpperCase()
        : null
    );

    (async () => {
      try {
        setLoading(true);
        const [active, promos, promoLookup] = await Promise.all([
          getActiveAgreement(agreementCode),
          getMyPromotions().catch(() => null),
          promoCode ? getPromotions({ promoCode }).catch(() => null) : null,
        ]);
        setAgreement(active);
        const results = (promos?.results || []).map(normalizePromo).filter(Boolean);
        const promoLookupResultsRaw = Array.isArray(promoLookup?.results)
          ? promoLookup.results
          : Array.isArray(promoLookup)
            ? promoLookup
            : promoLookup
              ? [promoLookup]
              : [];
        const promoLookupResults = promoLookupResultsRaw
          .map(normalizePromo)
          .filter(Boolean);
        const contextRole = workflowContext?.role || localStorage.getItem("user_role");
        const eligibleRoleRaw = agreementCode?.includes("HOST")
          ? "HOST"
          : agreementCode?.includes("ARTIST")
            ? "ARTIST"
            : contextRole;
        const eligibleRole = eligibleRoleRaw ? String(eligibleRoleRaw).toUpperCase() : null;
        const matching = results.find((item) => {
          const promoRole = item?.eligible_role ? String(item.eligible_role).toUpperCase() : null;
          const roleMatches = eligibleRole ? promoRole === eligibleRole : true;
          return roleMatches && (item?.commission_rate_override || item?.membership_fee_override_cents);
        });
        const fallback = results.find((item) =>
          item?.commission_rate_override || item?.membership_fee_override_cents
        );
        const promoFromCode =
          promoLookupResults.find(
            (item) =>
              item?.code === promoCode || item?.promotion?.code === promoCode
          ) || promoLookupResults[0];
        const selectedPromo = promoFromCode || matching || fallback || contextPromo || null;
        setPromo(selectedPromo);
        setPromoRole(
          selectedPromo?.eligible_role
            ? String(selectedPromo.eligible_role).toUpperCase()
            : eligibleRole
        );
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load agreement.");
      } finally {
        setLoading(false);
      }
    })();
  }, [
    open,
    agreementCode,
    promoCode,
    workflowContext?.role,
    workflowContext?.promo_preview,
    workflowContext?.promo_details,
    workflowContext?.promo_code,
  ]);

  const promoEndDate = getPromoEndDate(promo);
  const promoExpired = promo?.expires_at
    ? new Date(promo.expires_at) < new Date()
    : false;
  const promoCommissionLabel =
    promo?.commission_rate_override != null
      ? formatPercent(promo.commission_rate_override)
      : "5%";
  const promoMembershipFeeLabel =
    promo?.membership_fee_override_cents != null
      ? formatCurrencyFromCents(promo.membership_fee_override_cents)
      : null;
  const hasPromoMembershipFee =
    promoMembershipFeeLabel && Number(promo?.membership_fee_override_cents) !== 0;
  const promoMessage = (() => {
    if (!promo && !promoCode) return null;
    if (!promo || promoExpired) {
      return "Platform fee: 10% commission";
    }
    const endLabel = promoEndDate ? formatDate(promoEndDate) : "the end of the promotion";
    if (promo?.commission_rate_override == null && hasPromoMembershipFee) {
      return `Promotional deal --> Monthly membership fee: ${promoMembershipFeeLabel} until ${endLabel}`;
    }
    return `Promotional deal --> Platform fee: ${promoCommissionLabel} commission until ${endLabel} and 10% afterwards`;
  })();
  const pdfUrl = resolveAgreementPdfUrl(agreement?.pdf_url);

  const handleAccept = async () => {
    try {
      setError("");
      setLoading(true);
      const context = {
        ...workflowContext,
        ...(promo?.code ? { promo_code: promo.code } : {}),
      };
      await acceptAgreement(agreementCode, context);
      await onAccepted?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Acceptance failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={loading ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92%", sm: 520 },
          bgcolor: "background.paper",
          borderRadius: 3,
          p: 3,
          boxShadow: 24,
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          {title || "Agreement"}
        </Typography>

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

        {agreement && (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Version <b>{agreement.active_version}</b> (effective{" "}
              <b>{agreement.effective_date}</b>)
            </Typography>

            <Typography variant="body2" sx={{ mb: 2 }}>
              Please review and accept to continue.
            </Typography>

            {promoMessage ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {promoRole ? `Promotion for ${promoRole.toLowerCase()}` : "Promotion details"}:
                </Typography>
                <Typography variant="body2">{promoMessage}</Typography>
                {promo?.code ? (
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                    Promo code: {promo.code}
                  </Typography>
                ) : null}
              </Alert>
            ) : null}

            {pdfUrl ? (
              <>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    height: { xs: 360, sm: 420 },
                    overflow: "hidden",
                    mb: 1,
                  }}
                >
                  <iframe
                    src={pdfUrl}
                    title={`${title || "Agreement"} PDF`}
                    width="100%"
                    height="100%"
                    style={{ border: "none" }}
                  />
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Trouble viewing?{" "}
                  <Link href={pdfUrl} target="_blank" rel="noreferrer">
                    Open in new tab
                  </Link>
                  .
                </Typography>
              </>
            ) : null}

            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  disabled={loading}
                />
              }
              label="I have read and agree to the terms."
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
              <Button onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAccept}
                disabled={!checked || loading}
              >
                Accept & Continue
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}
